import { AnalyticsEvent, QueuedEvent } from '../types/analytics'

/**
 * EventQueue - Handles reliable event batching and delivery
 * 
 * Queues events locally, batches them for efficient sending, and handles
 * retry logic for failed deliveries. Includes deduplication based on event content.
 */
export class EventQueue {
  private queue: QueuedEvent[] = []
  private processing: boolean = false
  private flushTimer: NodeJS.Timeout | null = null
  private recentEventHashes: Map<string, number> = new Map() // For deduplication
  
  // Configuration
  private readonly MAX_QUEUE_SIZE = 100
  private readonly BATCH_SIZE = 10
  private readonly FLUSH_INTERVAL = 30 * 1000 // 30 seconds
  private readonly MAX_RETRIES = 3
  private readonly RETRY_DELAY = 5 * 1000 // 5 seconds
  private readonly STORAGE_KEY = 'analytics_event_queue'
  private readonly DEDUP_WINDOW = 10 * 1000 // 10 seconds for deduplication

  constructor() {
    // Load existing queue from storage
    this.loadQueueFromStorage()
    
    // Start periodic flushing
    this.scheduleFlush()
    
    // Handle browser close
    window.addEventListener('beforeunload', () => this.flush())
    
    // Clean up old hashes periodically
    setInterval(() => this.cleanupOldHashes(), 30 * 1000)
  }

  /**
   * Generate hash for event deduplication
   */
  private generateEventHash(event: AnalyticsEvent): string {
    // Create a stable representation of the event
    const eventForHashing = {
      eventType: event.eventType,
      action: event.action,
      toolName: event.toolName,
      // Include key properties but exclude timestamp and session-specific data
      properties: event.properties ? Object.keys(event.properties).sort().join(',') : '',
      // Round timestamp to nearest second to catch rapid duplicates
      timeWindow: Math.floor(Date.now() / 1000)
    }
    
    const hashString = JSON.stringify(eventForHashing)
    return btoa(hashString).replace(/[^a-zA-Z0-9]/g, '')
  }

  /**
   * Check if event is a duplicate
   */
  private isDuplicateEvent(event: AnalyticsEvent): boolean {
    const eventHash = this.generateEventHash(event)
    const now = Date.now()
    const lastSeen = this.recentEventHashes.get(eventHash)
    
    if (lastSeen && (now - lastSeen) < this.DEDUP_WINDOW) {
      console.log('🚫 EventQueue: Blocking duplicate event:', {
        eventType: event.eventType,
        action: event.action,
        toolName: event.toolName,
        hash: eventHash.substring(0, 10) + '...',
        timeSinceLastSeen: now - lastSeen
      })
      return true
    }

    // Record this event hash
    this.recentEventHashes.set(eventHash, now)
    return false
  }

  /**
   * Clean up old event hashes
   */
  private cleanupOldHashes(): void {
    const now = Date.now()
    const cutoff = now - this.DEDUP_WINDOW
    
    for (const [hash, timestamp] of this.recentEventHashes.entries()) {
      if (timestamp < cutoff) {
        this.recentEventHashes.delete(hash)
      }
    }
  }

  /**
   * Add event to queue
   */
  enqueue(event: AnalyticsEvent): void {
    // Check for duplicates first
    if (this.isDuplicateEvent(event)) {
      console.log('🚫 EventQueue: Skipping duplicate event')
      return // Don't enqueue duplicates
    }

    console.log('✅ EventQueue: Enqueueing new event:', {
      eventType: event.eventType,
      action: event.action,
      toolName: event.toolName
    })

    const queuedEvent: QueuedEvent = {
      event,
      timestamp: new Date().toISOString(),
      retryCount: 0
    }

    // Add to end of queue (FIFO)
    this.queue.push(queuedEvent)

    // Enforce queue size limit
    if (this.queue.length > this.MAX_QUEUE_SIZE) {
      // Remove oldest events
      this.queue.shift()
    }

    // Save to storage
    this.saveQueueToStorage()

    // Check if we should flush immediately
    if (this.queue.length >= this.BATCH_SIZE) {
      this.flush()
    }
  }

  /**
   * Flush queue immediately (send all pending events)
   */
  async flush(): Promise<void> {
    if (this.processing || this.queue.length === 0) return

    this.processing = true
    
    // Get batch to process outside try block so it's accessible in catch
    const batch = this.queue.splice(0, this.BATCH_SIZE)

    try {
      const events = batch.map(item => item.event)

      // Send to backend (placeholder for now)
      await this.sendEventsToBackend(events)

      // Success - events are already removed from queue
      console.log(`Successfully sent ${events.length} analytics events`)
      console.log(events)

    } catch (error) {
      // Failure - put events back in queue with retry logic
      this.handleFailedBatch(batch, error as Error)
    } finally {
      this.processing = false
      this.saveQueueToStorage()

      // Schedule next flush if queue not empty
      if (this.queue.length > 0) {
        setTimeout(() => this.flush(), this.RETRY_DELAY)
      }
    }
  }

  /**
   * Get current queue size
   */
  size(): number {
    return this.queue.length
  }

  /**
   * Clear all queued events
   */
  clear(): void {
    this.queue = []
    this.saveQueueToStorage()
  }

  /**
   * Send events to backend API
   */
  private async sendEventsToBackend(events: AnalyticsEvent[]): Promise<void> {
    // Get API endpoint from config
    let apiEndpoint: string
    try {
      const { config } = require('../config/environment')
      apiEndpoint = config.API_BASE_URL
    } catch {
      apiEndpoint = 'http://localhost:3000'
    }
    
    const response = await fetch(`${apiEndpoint}/api/analytics/events`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ events })
    })

    if (!response.ok) {
      throw new Error(`Analytics API error: ${response.status} ${response.statusText}`)
    }
  }

  /**
   * Handle failed batch - implement retry logic
   */
  private handleFailedBatch(batch: QueuedEvent[], error: Error): void {
    batch.forEach(queuedEvent => {
      queuedEvent.retryCount++

      if (queuedEvent.retryCount <= this.MAX_RETRIES) {
        // Put back in queue for retry
        this.queue.unshift(queuedEvent) // Add to front for immediate retry
      } else {
        // Max retries exceeded - log and discard
        console.error('Analytics event dropped after max retries:', queuedEvent.event, error)
        this.logFailedEvent(queuedEvent, error)
      }
    })
  }

  /**
   * Schedule periodic flushing
   */
  private scheduleFlush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer)
    }

    this.flushTimer = setTimeout(() => {
      this.flush()
      this.scheduleFlush() // Schedule next flush
    }, this.FLUSH_INTERVAL)
  }

  /**
   * Save queue to localStorage
   */
  private saveQueueToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.queue))
    } catch (error) {
      // Handle storage quota exceeded
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        // Remove oldest events and try again
        this.queue.splice(0, 10)
        this.saveQueueToStorage()
      } else {
        console.warn('Failed to save event queue to localStorage:', error)
      }
    }
  }

  /**
   * Load queue from localStorage
   */
  private loadQueueFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (!stored) return

      const parsed = JSON.parse(stored)
      this.queue = Array.isArray(parsed) ? parsed : []

      // Clean up old events (older than 24 hours)
      const dayAgo = Date.now() - (24 * 60 * 60 * 1000)
      this.queue = this.queue.filter(item => 
        new Date(item.timestamp).getTime() > dayAgo
      )

    } catch (error) {
      console.error('Failed to load event queue from storage:', error)
      this.queue = []
    }
  }

  /**
   * Log failed events for debugging
   */
  private logFailedEvent(queuedEvent: QueuedEvent, error: Error): void {
    // Store failed events in separate storage for debugging
    try {
      const failedEvents = JSON.parse(localStorage.getItem('analytics_failed_events') || '[]')
      failedEvents.push({
        ...queuedEvent,
        failedAt: new Date().toISOString(),
        error: error.message
      })
      
      // Keep only last 50 failed events
      if (failedEvents.length > 50) {
        failedEvents.splice(0, failedEvents.length - 50)
      }
      
      localStorage.setItem('analytics_failed_events', JSON.stringify(failedEvents))
    } catch (storageError) {
      console.error('Failed to log failed event:', storageError)
    }
  }
}

// Create singleton instance
export const eventQueue = new EventQueue()